import React from 'react';
import * as R from 'ramda';
import * as Yup from 'yup';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature fleet-profile
import { TableCell } from '../components/table-cells';
//////////////////////////////////////////////////

const intervalOptions = [
  {
    value: 'Weekly',
    label: 'Weekly',
  },
  {
    value: 'Monthly',
    label: 'Monthly',
  },
  {
    value: 'Quarterly',
    label: 'Quarterly',
  },
  {
    value: 'Annually',
    label: 'Annually',
  },
];

const settings = {
  [GC.FIELD_INSURANCE_TYPE]: {
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:insurance-type',
    options: 'insuranceTypeOptions',
    additionalInputWrapperStyles: { width: 400 },
    customComponent: (row) =>
      <TableCell text={R.path(['data', GC.FIELD_INSURANCE_TYPE, GC.FIELD_DISPLAYED_VALUE], row)} />,
    filter: {
      type: 'string',
      getFieldUiValue: R.path([GC.FIELD_INSURANCE_TYPE, GC.FIELD_DISPLAYED_VALUE]),
    },
  },
  [GC.FIELD_EFFECTIVE_DATE]: {
    isRequired: true,
    type: 'datePicker',
    filter: { type: 'date' },
    name: 'titles:effective-date',
  },
  [GC.FIELD_EXPIRATION_DATE]: {
    isRequired: true,
    type: 'datePicker',
    filter: { type: 'date' },
    name: 'titles:expiration-date',
  },
  [GC.FIELD_PAYMENT_AMOUNT]: {
    width: 150,
    isRequired: true,
    name: 'titles:payment',
    filter: { type: 'number' },
  },
  [GC.FIELD_PAYMENT_INTERVAL]: {
    width: 180,
    isRequired: true,
    type: 'reactSelect',
    options: intervalOptions,
    name: 'titles:payment-interval',
    filter: { type: 'selectMultiple', options: intervalOptions },
  },
  [GC.FIELD_DESCRIPTION]: {
    type: 'textarea',
    inputStyles: { p: 15 },
    name: 'titles:description',
    additionalInputWrapperStyles: { width: 400 },
  },
};

const validationSchema = Yup.object().shape({
  ...R.map(({ isRequired }) => G.ifElse(isRequired, G.yupStringRequired, G.yupStringNotRequired), settings),
  [GC.FIELD_DESCRIPTION]: Yup.string()
    .nullable(true)
    .max(2000, G.getShouldBeFromToCharLocaleTxt(0, 2000)),
  [GC.FIELD_PAYMENT_AMOUNT]: Yup.number()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .typeError(G.getShouldBeNumericLocaleTxt())
    .min(0, G.getShouldBeFromToLocaleTxt(0, 1000000000))
    .max(1000000000, G.getShouldBeFromToLocaleTxt(0, 1000000000))
    .transform((value) => G.ifElse(R.equals(value, ''), null, value)),
});

const defaultInputWrapperStyles = {
  mb: 25,
  width: 190,
};

const defaultReportFields = R.keys(settings);

const report = {
  [GC.FIELD_TYPE]: GC.TRUCK_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.TRUCK_PROFILE_UI_REPORTS}Insurance`,
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), defaultReportFields),
};

const defaultValues = R.map(() => null, settings);

const fieldSettings = R.compose(
  R.values,
  R.mapObjIndexed(({
    name,
    options,
    inputStyles,
    type = 'text',
    locationMapper,
    isRequired = false,
    additionalInputWrapperStyles,
  }, fieldName) => ({
    type,
    options,
    fieldName,
    isRequired,
    inputStyles,
    locationMapper,
    label: R.of(Array, name),
    inputWrapperStyles: {
      ...defaultInputWrapperStyles,
      ...G.spreadUiStyles(additionalInputWrapperStyles),
    },
  })),
)(settings);

const columnSettings = R.map(R.pick(['name', 'width', 'filter', 'sortable', 'customComponent']), settings);

export const truckInsuranceSettings = {
  report,
  defaultValues,
  fieldSettings,
  columnSettings,
  validationSchema,
  defaultReportFields,
  groupName: 'insurances',
  withResizableColumns: true,
  withUiFiltersAndEditView: true,
  includeItemListToTablePropsMemoDeps: true,
  itemTitleArr: ['titles:insurance', 'Insurance'],
  additionalTableSettings: { searchableTitles: true },
  formGroupTitleArr: ['titles:insurance', 'Insurance'],
  endpoints: {
    list: 'truckInsuranceList',
    createOrUpdate: 'truckInsurance',
    remove: 'getCurrentTruckInsuranceEndpoint',
  },
  fieldsWrapperStyles: {
    width: 400,
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    insuranceTypeOptions: R.drop(1, R.pathOr([], ['dropdownOptions', GC.TRUCK_INSURANCE_TYPE], configGroup)),
  }),
  makeInitialValues: (initialValues) => {
    const { insuranceType } = initialValues;

    return R.assoc(GC.FIELD_INSURANCE_TYPE, G.getDropdownOptionGuidFromObject(insuranceType), initialValues);
  },
};

export const trailerInsuranceSettings = {
  ...truckInsuranceSettings,
  report: {
    ...report,
    [GC.FIELD_TYPE]: GC.TRAILER_PROFILE_UI_REPORTS,
    [GC.FIELD_GUID]: `${GC.TRAILER_PROFILE_UI_REPORTS}Insurance`,
  },
  endpoints: {
    list: 'trailerInsuranceList',
    createOrUpdate: 'trailerInsurance',
    remove: 'getCurrentTrailerInsuranceEndpoint',
  },
  // helpers
  makeOptionsForSelect: ({ configGroup }) => ({
    insuranceTypeOptions: R.drop(1, R.pathOr([], ['dropdownOptions', GC.TRAILER_INSURANCE_TYPE], configGroup)),
  }),
};
